import { IXmlNode } from '@/app-modules/server-communications';
import { IPrizeStructure, IPrizeStake } from '@/components/game/implementations/_default/_domain/models';
import { CatchUpData, ResetData, CallsData, NextGameData, PrizeData } from '@/components/game/_domain/server/prize/prize-data';
import { Bingo50PrizeType, getPrizeTypeName } from '@/components/game/implementations/uk/bingo-50/_domain/models';
import { getGameConfiguration } from '@/components/game/_domain/helpers';
import { ConfigurationPaths } from '@/app-modules/config/models';

class Bingo50PrizeData extends PrizeData {
  constructor(node: IXmlNode) {
    super(node);
  }

  private get stakeValues(): number[] {
    return [25, 50, 75, 100];
  }

  private get prizeTypes(): Bingo50PrizeType[] {
    return [
      Bingo50PrizeType.Jackpot,
      Bingo50PrizeType.HousePrize,
      Bingo50PrizeType.LinePrize
    ];
  }

  private get jackpotCall(): number {
    const gameConfiguration = getGameConfiguration();
    const gameConfigurationPaths = ConfigurationPaths.forGame('bingo50');
    return Number(gameConfiguration.getConfigurationByPath(gameConfigurationPaths.jackpotcalls));
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    return {
      stakes: this.stakeValues.map((stakeValue) => this.getPrizeStakeForStakeValue(stakeValue, node))
    };
  }

  private getPrizeStakeForStakeValue(stakeValue: number, node: IXmlNode): IPrizeStake {
    return {
      stakeValue,
      ticketBooks: [
        {
          id: 'default',
          name: 'Default',
          prizes: this.prizeTypes.map((prizeType) => {
            const isJackpot = prizeType === Bingo50PrizeType.Jackpot;

            return {
              name: getPrizeTypeName(prizeType),
              prize: this.getPrizeValueForPrizeTypeAndStakeValue(prizeType, stakeValue, node),
              disablePrize: false,
              isJackpot,
              jackpotCalls: isJackpot ? this.jackpotCall : 0
            };
          })
        }
      ]
    };
  }

  private getPrizeValueForPrizeTypeAndStakeValue(prizeType: Bingo50PrizeType, stakeValue: number, node: IXmlNode): number {
    if (prizeType === Bingo50PrizeType.Jackpot) {
      return stakeValue * 1000;
    }
    const prizeAttributeName = this.getPrizeAttributeNameForPrizeTypeAndStakeValue(prizeType, stakeValue);
    return Number(node.attributes[prizeAttributeName]);
  }

  protected getPrizeAttributeNameForPrizeTypeAndStakeValue(prizeType: Bingo50PrizeType, stakeValue: number) {
    return `${prizeType}${stakeValue}`;
  }
}

class Bingo50PrizeDataForNextGame extends Bingo50PrizeData {
  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeAttributeNameForPrizeTypeAndStakeValue(prizeType: Bingo50PrizeType, stakeValue: number) {
    let prizeTypeSpecifier: string;
    switch (prizeType) {
      case Bingo50PrizeType.HousePrize:
        prizeTypeSpecifier = 'h';
        break;
      case Bingo50PrizeType.Jackpot:
        prizeTypeSpecifier = 'j';
        break;
      case Bingo50PrizeType.LinePrize:
        prizeTypeSpecifier = 'l';
        break;
    }

    return `prize${stakeValue}${prizeTypeSpecifier}`;
  }
}

class Bingo50CatchUpData extends CatchUpData {
  private prizeData: Bingo50PrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new Bingo50PrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class Bingo50ResetData extends ResetData {
  private prizeData: Bingo50PrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new Bingo50PrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class Bingo50CallsData extends CallsData {
  constructor(node: IXmlNode) {
    super(node);
  }
}

class Bingo50NextGameData extends NextGameData {
  private prizeData: Bingo50PrizeDataForNextGame;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new Bingo50PrizeDataForNextGame(node);
    }
    return this.prizeData.prizeStructure;
  }
}

export {
  Bingo50CatchUpData,
  Bingo50ResetData,
  Bingo50CallsData,
  Bingo50NextGameData
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/bingo-50/_domain/models/prize.ts